<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import { Icon } from '@iconify/vue'
import VideoPicker from '@/onenetwork/components/video_picker/VideoPicker.vue'

interface Props {
  form: any
  editable: boolean
  newData?: any
}

const props = defineProps<Props>()

const editable = computed(() => {
  return props.editable
})

const initValues = computed<any>(() => {
  return props.form?.initialValues || {}
})

const photoList = ref<any[]>([])
watch(
  () => initValues.value,
  (newValue: any) => {
    if (newValue) {
      photoList.value = Array.isArray(newValue.photos) ? [...newValue.photos] : []
    }
  },
  { immediate: true },
)

const video = ref<any>({})
watch(
  () => initValues.value,
  (newValue: any) => {
    if (newValue) {
      video.value = newValue.video || {}
    }
  },
  { immediate: true },
)

const panorama = ref<any>({})
watch(
  () => initValues.value,
  (newValue: any) => {
    if (newValue) {
      panorama.value = newValue.panorama || {}
    }
  },
  { immediate: true },
)

const imageUuid = ref(uuid.v4().toString())

const syncPhotosToForm = () => {
  props.form?.setFieldValue('photos', [...photoList.value])
}

const handleChangeImage = (data: any) => {
  let newImage = data

  if (data?.response?.data) {
    newImage = data.response.data
    imageUuid.value = uuid.v4().toString()
  }

  if (!newImage?.uuid) return

  const index = photoList.value.findIndex((photo: any) => photo.uuid === newImage.uuid)

  if (index !== -1) {
    photoList.value[index] = {
      ...photoList.value[index],
      ...newImage,
    }
  } else {
    photoList.value.push(newImage)
  }

  syncPhotosToForm()
}

const handleRemoveImage = (imageUuid: string) => {
  const index = photoList.value.findIndex((photo: any) => photo.uuid === imageUuid)
  if (index !== -1) {
    photoList.value.splice(index, 1)
    syncPhotosToForm()
  }
}

const handleDropImage = (payload: any) => {
  const fromUuid = payload?.from?.uuid
  const toUuid = payload?.to?.uuid

  if (!fromUuid || !toUuid || fromUuid === toUuid) return

  const fromIndex = photoList.value.findIndex((photo: any) => photo.uuid === fromUuid)
  const toIndex = photoList.value.findIndex((photo: any) => photo.uuid === toUuid)

  if (fromIndex === -1 || toIndex === -1) return

  const nextPhotos = [...photoList.value]
  const draggedItem = nextPhotos[fromIndex]

  nextPhotos.splice(fromIndex, 1)
  nextPhotos.splice(toIndex, 0, draggedItem)

  photoList.value = nextPhotos
  syncPhotosToForm()
}

const handleChangePanorama = (data: any) => {
  panorama.value = data.response.data
  props.form?.setFieldValue('panorama', data.response.data)
}

const handleRemovePanorama = () => {
  panorama.value = {}
  props.form?.setFieldValue('panorama', {})
}

const handleChangeVideo = (data: any) => {
  video.value = data
  props.form?.setFieldValue('video', data.response.data)
}

const handleRemoveVideo = () => {
  video.value = {}
  props.form?.setFieldValue('video', {})
}

const newData = computed(() => {
  return props.newData || {}
})

const newPhototList = computed(() => {
  return Array.isArray(newData.value?.photos) ? newData.value.photos : []
})

const newPanorama = computed(() => {
  return newData.value?.panorama || {}
})

const newVideo = computed(() => {
  return newData.value?.video || {}
})
</script>

<template>
  <div class="flex flex-col w-full gap-y-5 listing-media">
    <div class="flex flex-col w-full md:flex-3">
      <div class="flex w-full py-3">
        <div class="flex-1 text-right">
          <div class="font-bold">Hình ảnh</div>
        </div>

        <div class="flex-5">
          <div class="listing-media-list">
            <InputText type="text" hidden name="photos" />

            <div
              class="listing-media-list__photo-item"
              v-for="(i, index) in photoList"
              :key="`image-item-${i.uuid || index}`"
            >
              <div class="relative">
                <div
                  class="absolute top-1 left-1 bg-red-100 text-red-900 px-2 py-1 z-10 rounded"
                  v-if="
                    newPhototList?.length && !newPhototList?.map((e: any) => e.id).includes(i.id)
                  "
                >
                  Xoá
                </div>

                <image-picker
                  readonly
                  type="photo"
                  :uuid="i.uuid"
                  folder="real_estate"
                  width="100%"
                  height="150px"
                  :src="i.url?.medium || i.name"
                  :previewSrc="i.url?.original"
                  :draggable="editable"
                  @action:drop="handleDropImage"
                />

                <div
                  class="listing-media-list__photo-remove-item"
                  v-if="editable"
                  @click="() => handleRemoveImage(i.uuid)"
                >
                  <Icon icon="zondicons:close-solid" />
                </div>
              </div>
            </div>

            <template v-if="newPhototList?.length">
              <div
                class="listing-media-list__photo-item"
                v-for="(ni, index) in (newPhototList || []).filter(
                  (e: any) => !photoList.map((p: any) => p.id).includes(e.id),
                )"
                :key="`image-new-${ni.uuid || ni.id || index}`"
              >
                <div class="relative">
                  <div
                    class="absolute top-1 left-1 bg-green-100 text-green-900 px-2 py-1 z-10 rounded"
                  >
                    Thêm
                  </div>
                  <image-picker
                    readonly
                    type="photo"
                    :uuid="ni.uuid"
                    folder="real_estate"
                    width="100%"
                    height="150px"
                    :src="ni.url?.medium || ni.name"
                    :previewSrc="ni.url?.original"
                  />
                </div>
              </div>
            </template>

            <div class="listing-media-list__photo-item add_photo" v-if="editable">
              <image-picker
                type="photo"
                :uuid="imageUuid"
                folder="real_estate"
                width="100%"
                height="150px"
                @action:upload-success="handleChangeImage"
                @action:choose-image="handleChangeImage"
                upload
                previewIcon="proicons:photo-add"
                hideUploadPreview
                hideRemove
              />
            </div>
          </div>

          <span class="font-italic text-[12px] text-red-500">
            {{ props.form?.fields?.photos?.states?.error?.message }}
          </span>
        </div>
      </div>

      <Divider />

      <div class="flex w-full py-3">
        <div class="flex-1 text-right">
          <div class="font-bold">Panorama</div>
        </div>
        <div class="flex-5">
          <div class="listing-media-list">
            <div class="listing-media-list__photo-item">
              <InputText type="text" hidden name="panorama" v-model="panorama" />
              <div class="relative">
                <div
                  class="absolute top-1 left-1 bg-red-100 text-red-900 px-2 py-1 z-10 rounded"
                  v-if="panorama?.id && newPanorama?.id && panorama?.id !== newPanorama?.id"
                >
                  Xoá
                </div>
                <image-picker
                  :readonly="!editable"
                  type="panorama"
                  :uuid="uuid.v4().toString()"
                  folder="real_estate"
                  width="100%"
                  height="150px"
                  :upload="true"
                  @action:upload-success="handleChangePanorama"
                  :src="panorama?.url?.original"
                  :previewSrc="panorama?.url?.original"
                  previewIcon="proicons:photo-add"
                />
                <div
                  class="listing-media-list__photo-remove-item"
                  v-if="editable && panorama?.url"
                  @click="handleRemovePanorama"
                >
                  <Icon icon="zondicons:close-solid" />
                </div>
              </div>
            </div>

            <template v-if="newPanorama?.id && panorama?.id !== newPanorama?.id">
              <div class="listing-media-list__photo-item">
                <div class="relative">
                  <div
                    class="absolute top-1 left-1 bg-green-100 text-green-900 px-2 py-1 z-10 rounded"
                  >
                    Thêm
                  </div>
                  <image-picker
                    readonly
                    type="panorama"
                    :uuid="newPanorama.uuid"
                    folder="real_estate"
                    width="100%"
                    height="150px"
                    :src="newPanorama.url?.original"
                    :previewSrc="newPanorama.url?.original"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <Divider />

      <div class="flex w-full py-3">
        <div class="flex-1 text-right">
          <div class="font-bold">Video</div>
        </div>
        <div class="flex-5">
          <div class="listing-media-list">
            <div class="listing-media-list__video-item">
              <InputText type="text" hidden name="video" />
              <div class="relative">
                <div
                  class="absolute top-1 left-1 bg-red-100 text-red-900 px-2 py-1 z-10 rounded"
                  v-if="video?.id && newVideo?.id && video?.id !== newVideo?.id"
                >
                  Xoá
                </div>
                <video-picker
                  :readonly="!editable"
                  type="video"
                  :uuid="uuid.v4().toString()"
                  folder="real_estate"
                  width="100%"
                  height="150px"
                  :upload="true"
                  @action:upload-success="handleChangeVideo"
                  :src="video?.url?.thumbnail"
                  :previewSrc="video.url?.original"
                />
                <div
                  class="listing-media-list__video-remove-item"
                  v-if="editable && video?.url"
                  @click="handleRemoveVideo"
                >
                  <Icon icon="zondicons:close-solid" />
                </div>
              </div>
            </div>

            <template v-if="newVideo?.id && video?.id !== newVideo?.id">
              <div class="listing-media-list__video-item">
                <div class="relative">
                  <div
                    class="absolute top-1 left-1 bg-green-100 text-green-900 px-2 py-1 z-10 rounded"
                  >
                    Thêm
                  </div>
                  <video-picker
                    readonly
                    type="video"
                    :uuid="newVideo.uuid"
                    folder="real_estate"
                    width="100%"
                    height="150px"
                    :src="newVideo?.url?.thumbnail"
                    :previewSrc="newVideo.url?.original"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <Divider />
    </div>
  </div>
</template>

<style scoped></style>
